import { z } from 'zod';

export const CANONICAL_SKILL_REVISION_VERSION = 'skill-md-primary-v1' as const;
export const SKILL_REVISION_CONTENT_SCOPE = 'primary_skill_file' as const;
export const SKILL_REVISION_UNKNOWN_REASONS = [
  'unsupported_include',
  'missing_primary',
  'invalid_utf8',
  'oversized',
  'outside_approved_root',
  'not_regular_file',
  'changed_during_read',
  'ambiguous_deployment',
  'io_error',
  'legacy_schema',
  'unsupported_version',
] as const;

export type SkillRevisionUnknownReason = (typeof SKILL_REVISION_UNKNOWN_REASONS)[number];

const lowercaseHex64 = z.string().regex(/^[a-f0-9]{64}$/);
const revisionCommon = {
  canonicalizationVersion: z.literal(CANONICAL_SKILL_REVISION_VERSION),
  contentScope: z.literal(SKILL_REVISION_CONTENT_SCOPE),
} as const;

export const SignalRevisionObservationSchema = z.discriminatedUnion('state', [
  z.strictObject({
    state: z.literal('known'),
    ...revisionCommon,
    contentHash: lowercaseHex64,
  }),
  z.strictObject({
    state: z.literal('unknown_revision'),
    ...revisionCommon,
    reason: z.enum(SKILL_REVISION_UNKNOWN_REASONS),
  }),
]);
export type SignalRevisionObservation = z.infer<typeof SignalRevisionObservationSchema>;

export const SkillRevisionIdentitySchema = z.discriminatedUnion('state', [
  z.strictObject({
    state: z.literal('known'),
    skillId: z.number().int().positive(),
    ...revisionCommon,
    contentHash: lowercaseHex64,
    observedAt: z.iso.datetime(),
  }),
  z.strictObject({
    state: z.literal('unknown_revision'),
    skillId: z.number().int().positive(),
    ...revisionCommon,
    reason: z.enum(SKILL_REVISION_UNKNOWN_REASONS),
    observedAt: z.iso.datetime(),
  }),
]);
export type SkillRevisionIdentity = z.infer<typeof SkillRevisionIdentitySchema>;

const canonicalUuidV4 = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
const legacyCanonicalUuidV4 = z
  .string()
  .transform((value) => value.toLowerCase())
  .pipe(canonicalUuidV4);
const installationSecret = z
  .string()
  .regex(
    /^[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/,
    'must be canonical unpadded base64url encoding of exactly 32 bytes'
  );
const bindingCommon = {
  bindingVersion: z.literal(1),
  installationId: canonicalUuidV4,
} as const;

export const SignalInstallationClaimSchema = z.strictObject({
  ...bindingCommon,
  installationSecret,
});
export type SignalInstallationClaim = z.infer<typeof SignalInstallationClaimSchema>;

export const SignalInstallationBindingSchema = z.strictObject({
  state: z.literal('bound'),
  ...bindingCommon,
  epoch: z.number().int().positive(),
});
export type SignalInstallationBinding = z.infer<typeof SignalInstallationBindingSchema>;

export const SignalInstallationProofSchema = z.strictObject({
  ...bindingCommon,
  installationSecret,
  epoch: z.number().int().positive(),
});
export type SignalInstallationProof = z.infer<typeof SignalInstallationProofSchema>;

/** Staged compatibility contract for the already-shipped UUID-only client. */
export const LegacySignalInstallationClaimSchema = z.strictObject({
  installationId: legacyCanonicalUuidV4,
});
export type LegacySignalInstallationClaim = z.infer<typeof LegacySignalInstallationClaimSchema>;

export const LegacySignalInstallationResponseSchema = z.strictObject({
  installationId: canonicalUuidV4,
  bound: z.boolean(),
});
export type LegacySignalInstallationResponse = z.infer<
  typeof LegacySignalInstallationResponseSchema
>;
/** Explicit alias for API/controller consumers that name the binding operation. */
export const LegacySignalInstallationBindingResponseSchema = LegacySignalInstallationResponseSchema;
export type LegacySignalInstallationBindingResponse = LegacySignalInstallationResponse;

export const CaptureHealthSchema = z.strictObject({
  lifecycle: z.enum(['disabled', 'awaiting_consent', 'collecting', 'revoking', 'error']),
  consent: z.strictObject({
    state: z.enum(['current', 'required', 'stale']),
    schemaVersion: z.number().int().positive().nullable(),
  }),
  runtime: z.strictObject({
    state: z.enum(['available', 'unavailable', 'unsupported']),
    adapter: z.enum(['claude_code', 'none']),
  }),
  hook: z.strictObject({ state: z.enum(['installed', 'missing', 'stale', 'error']) }),
  queue: z.strictObject({
    state: z.enum(['healthy', 'backlogged', 'error']),
    pendingBucket: z.enum(['none', 'one_to_ten', 'eleven_to_fifty', 'over_fifty']),
    oldestAgeBucket: z.enum(['none', 'under_hour', 'one_to_six_hours', 'over_six_hours']),
  }),
  revision: z.strictObject({
    state: z.enum(['known', 'unknown_revision', 'unobserved']),
  }),
  installation: z.strictObject({
    state: z.enum(['bound', 'unbound', 'mismatch', 'stale']),
  }),
  checkedAt: z.iso.datetime(),
});
export type CaptureHealth = z.infer<typeof CaptureHealthSchema>;

export const CaptureHealthReadRequestSchema = z.strictObject({});
export const CaptureHealthReadRequest = CaptureHealthReadRequestSchema;
export type CaptureHealthReadRequest = z.infer<typeof CaptureHealthReadRequestSchema>;
export const CaptureHealthReadResponseSchema = CaptureHealthSchema;
export const CaptureHealthReadResponse = CaptureHealthReadResponseSchema;
export type CaptureHealthReadResponse = CaptureHealth;

export const CAPTURE_DIAGNOSIS_CODES = [
  'healthy',
  'consent_required',
  'consent_stale',
  'runtime_unavailable',
  'unsupported_adapter',
  'hooks_missing',
  'hooks_stale',
  'queue_backlogged',
  'revision_unknown',
  'installation_mismatch',
  'local_error',
] as const;

export const CaptureDoctorResultSchema = z.strictObject({
  health: CaptureHealthSchema,
  diagnoses: z
    .array(
      z.strictObject({
        code: z.enum(CAPTURE_DIAGNOSIS_CODES),
        severity: z.enum(['info', 'warning', 'error']),
        remediation: z.enum([
          'none',
          'run_local_enable',
          'repair_local_hooks',
          'retry_local_operation',
          'rebind_local_installation',
          'remove_unsupported_dependency',
        ]),
      })
    )
    .max(16),
});
export type CaptureDoctorResult = z.infer<typeof CaptureDoctorResultSchema>;
export const CaptureDoctorResult = CaptureDoctorResultSchema;

export const CaptureDoctorRequestSchema = z.strictObject({});
export const CaptureDoctorRequest = CaptureDoctorRequestSchema;
export type CaptureDoctorRequest = z.infer<typeof CaptureDoctorRequestSchema>;

export const CaptureLifecycleRequestSchema = z.strictObject({
  action: z.enum(['enable', 'revoke']),
  confirmation: z.enum(['controlling_terminal_confirmed', 'unavailable_to_model']),
});
export type CaptureLifecycleRequest = z.infer<typeof CaptureLifecycleRequestSchema>;

export const CaptureLifecycleResultSchema = z.strictObject({
  action: z.enum(['enable', 'revoke']),
  state: z.enum([
    'completed',
    'declined',
    'requires_user_confirmation',
    'unsupported_adapter',
    'failed',
    'local_complete_server_pending',
  ]),
  localState: z.enum(['disabled', 'collecting', 'unchanged']),
  reasonCode: z.enum([
    'none',
    'controlling_terminal_required',
    'user_declined',
    'adapter_not_supported',
    'installation_mismatch',
    'hook_operation_failed',
    'server_unavailable',
    'local_operation_failed',
  ]),
  nextAction: z.enum([
    'none',
    'run_local_cli_independently',
    'retry_local_operation',
    'repair_local_hooks',
    'rebind_local_installation',
  ]),
});
export type CaptureLifecycleResult = z.infer<typeof CaptureLifecycleResultSchema>;

export const CaptureRequirementsSchema = z.strictObject({
  capabilityVersion: z.literal('1.0.0'),
  execution: z.literal('local_execution_required'),
  localState: z.literal('unknown'),
  documentationCategory: z.literal('local_capture_setup'),
});
export type CaptureRequirements = z.infer<typeof CaptureRequirementsSchema>;
